import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import { MotifFormField, MotifButton } from '@ey-xd/motif-react';
import { addAttribute } from '../../common/Attribute';

const AnalyticsReport = ({
  clients = [],
  analyticsReports = [],
  analyticsREITs = [],
  analyticsReportPeriods = [],
  selectedClientId,
  selectedReportId,
  onChangeClient,
  onChangeReport,
  onChangeReits,
  resetCheckboxes,
  change,
}) => {
  useEffect(() => {
    addAttribute("button[role = 'combobox']", 'type', 'button');
  }, []);

  const [selectedReitIds, setSelectedReitIds] = useState([]);
  const [selectedReportPeriodIds, setSelectedReportPeriodIds] = useState([]);

  const clientsOptions = clients.map((c) => {
    return { key: c.clientID, value: c.clientID, text: c.clientName };
  });

  const reportOptions = analyticsReports.map((report) => {
    const wsGuid =
      report.WorkspaceGuid ??
      report.workspaceGuid ??
      report.WorkspaceGUID ??
      report.workspaceGUID;
    const combinedId = [report.analyticsReportGUID, wsGuid]
      .filter(Boolean)
      .join('|');
    return {
      key: combinedId,
      value: combinedId,
      text: report.analyticsReportName,
    };
  });

  const reitOptions = analyticsREITs.map((reit) => {
    return { key: reit.reitid, value: reit.reitid, text: reit.reitName };
  });

  const parseQuarterYear = (s) => {
    if (typeof s !== 'string') return null;
    const m1 = s.match(/Q([1-4])\s+(\d{4})/i);
    if (m1) {
      return { quarter: parseInt(m1[1], 10), year: parseInt(m1[2], 10) };
    }
    const m2 = s.match(/(\d{4}).*Q([1-4])/i);
    if (m2) {
      return { quarter: parseInt(m2[2], 10), year: parseInt(m2[1], 10) };
    }
    return null;
  };

  const sortedAnalyticsReportPeriods = Array.isArray(analyticsReportPeriods)
    ? [...analyticsReportPeriods].sort((a, b) => {
        const pa = parseQuarterYear(a.reportPeriodDescription);
        const pb = parseQuarterYear(b.reportPeriodDescription);
        if (pa && pb) {
          const av = pa.year * 4 + pa.quarter;
          const bv = pb.year * 4 + pb.quarter;
          return bv - av; // latest first
        }
        return String(b.reportPeriodDescription).localeCompare(
          String(a.reportPeriodDescription)
        );
      })
    : [];

  const reportPeriodOptions = sortedAnalyticsReportPeriods.map((rp) => ({
    key: rp.reportPeriodID,
    value: rp.reportPeriodDescription,
    text: rp.reportPeriodDescription,
  }));

  useEffect(() => {
    setSelectedReitIds([]);
    setSelectedReportPeriodIds([]);
  }, [resetCheckboxes]);

  // Clear all selections when client changes and disable report period until REITs are selected
  useEffect(() => {
    if (typeof change === 'function') {
      change('analyticsReport', null);
      change('reit', []);
      change('reportPeriod', []);
    }
    setSelectedReitIds([]);
    setSelectedReportPeriodIds([]);
  }, [selectedClientId]);

  const handleReitChange = (event) => {
    if (!Array.isArray(event) || event.length === 0) {
      // Clear REIT selection
      setSelectedReitIds([]);
      // Also clear Report Period selection when REIT is unselected
      setSelectedReportPeriodIds([]);
      if (typeof change === 'function') {
        change('reportPeriod', []);
      }
      if (typeof onChangeReits === 'function') {
        onChangeReits([]);
      }
      return;
    }
    const nextSelectedReitIds = [];
    for (let i = 0; i < event.length; i++) {
      const value = event[i];
      if (value !== null && value !== undefined) {
        nextSelectedReitIds.push(String(value));
      }
    }
    setSelectedReitIds(nextSelectedReitIds);
    if (typeof onChangeReits === 'function') {
      onChangeReits(nextSelectedReitIds);
    }
  };

  const handleReportChange = (e, val) => {
    if (typeof onChangeReport === 'function') {
      onChangeReport(val);
    }
  };

  const handleReportPeriodChange = (event) => {
    if (!Array.isArray(event) || event.length === 0) {
      setSelectedReportPeriodIds([]);
      return;
    }
    const nextSelectedIds = [];
    for (let i = 0; i < event.length; i++) {
      const value = event[i];
      if (value !== null && value !== undefined) {
        nextSelectedIds.push(String(value));
      }
    }
    setSelectedReportPeriodIds(nextSelectedIds);
  };

  const isRunEnabled =
    Boolean(selectedClientId) &&
    Boolean(selectedReportId) &&
    Array.isArray(selectedReitIds) &&
    selectedReitIds.length > 0 &&
    Array.isArray(selectedReportPeriodIds) &&
    selectedReportPeriodIds.length > 0;

  return (
    <div className="container-fluid">
      <div className="row mt-3 mb-2 analytics-report-row">
        <MotifFormField>
          <div className="row ">
            <div className="col-md">
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Field
                  name="clientName"
                  label="Client Name (Required)"
                  isRequiredField={true}
                  validate={[formValidators.required]}
                  options={clientsOptions}
                  onChange={(e, val) => onChangeClient(val)}
                  component={formHelpers.SingleSelectComponent}
                  testingData={{ label: 'lblClientName', div: 'divClientName' }}
                  data-testid="btnClientName"
                />
              </div>
            </div>

            <div className="col-md">
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Field
                  name="analyticsReport"
                  label="Select Analytics Report (Required)"
                  isRequiredField={true}
                  validate={[formValidators.required]}
                  options={reportOptions}
                  disabled={!selectedClientId}
                  optionText={(r) => r.text}
                  optionValue={(r) => r.value}
                  component={formHelpers.SingleSelectComponent}
                  onChange={handleReportChange}
                  testingData={{
                    label: 'lblSelectAnalyticsReport',
                    div: 'divSelectAnalyticsReport',
                  }}
                  data-testid="btnSelectAnalyticsReport"
                />
              </div>
            </div>
            <div className="col-md">
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Field
                  name="reit"
                  label="Select REIT(s) (Required)"
                  isRequiredField={true}
                  validate={[formValidators.required]}
                  options={reitOptions}
                  optionText={(r) => r.text}
                  optionValue={(r) => r.value}
                  disabled={!selectedClientId}
                  component={formHelpers.MultipleSelect}
                  onChange={handleReitChange}
                  testingData={{
                    label: 'lblSelectREITs',
                    div: 'divSelectREITs',
                  }}
                  showSelectAllButton={reitOptions.length > 0}
                  data-testid="btnSelectREITs"
                />
              </div>
            </div>

            <div className="col-md">
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Field
                  name="reportPeriod"
                  label="Select Report Period(s) (Required)"
                  isRequiredField={true}
                  validate={[formValidators.required]}
                  options={reportPeriodOptions}
                  optionText={(rp) => rp.text}
                  optionValue={(rp) => rp.value}
                  optionKey={(r) => r.key}
                  disabled={
                    !(
                      Array.isArray(selectedReitIds) &&
                      selectedReitIds.length > 0
                    )
                  }
                  component={formHelpers.MultipleSelect}
                  onChange={handleReportPeriodChange}
                  testingData={{
                    label: 'lblSelectReportPeriod',
                    div: 'divSelectReportPeriod',
                  }}
                  showSelectAllButton={reportPeriodOptions.length > 0}
                  data-testid="btnSelectReportPeriod"
                />
              </div>
            </div>

            <div className="col-md-auto mt-1">
              <MotifButton
                disabled={!isRunEnabled}
                variant="primary"
                data-testid="btnRunReport"
              >
                Run Report
              </MotifButton>
            </div>
          </div>
        </MotifFormField>
      </div>
      <div className="row mt-5 mb-3 analytics-report-placeholder">
        <p>
          <b>
            Select Analytics Report -&gt; Select REIT(s) -&gt; Select Report
            Period(s) -&gt; Run Report.
          </b>
        </p>
      </div>
    </div>
  );
};

AnalyticsReport.propTypes = {
  clients: PropTypes.array,
  analyticsReports: PropTypes.array,
  analyticsREITs: PropTypes.array,
  analyticsReportPeriods: PropTypes.array,
  selectedClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChangeClient: PropTypes.func,
  onChangeReport: PropTypes.func,
  onChangeReits: PropTypes.func,
  resetCheckboxes: PropTypes.bool,
  change: PropTypes.func,
};

export default reduxForm({
  form: 'analyticsReportForm',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(AnalyticsReport);
